import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import palette from "../../common/styles/palette";
import { renderTextoComNegrito } from "./MensagemArquivos";
import { GapPriority, InterviewGap } from "../../types/Interview";

/**
 * O painel "O QUE FALTA" — a lista de Lacunas, agora ACIONÁVEL.
 *
 * POR QUE ELE FOI REFEITO. A versão anterior desenhava `gaps.slice(0, 6)` como
 * `<Box>` sem `onClick`, com `whiteSpace: "nowrap"` cortando a pergunta na
 * primeira palavra útil, e um "+7 outros pontos" que era texto morto. O cliente
 * descreveu o defeito melhor do que qualquer ticket: «acima da conversa temos
 * uma seção O QUE FALTA mas em nenhum momento isso se conecta com a conversa —
 * não há nenhum gancho/click/botão». Estava certo. O painel sabia o que faltava
 * e não oferecia NENHUM caminho para resolver; a única Lacuna com ação era a
 * corrente, e a ação era dispensá-la.
 *
 * O QUE MUDA. Cada linha passa a ser uma pergunta legível (2–3 linhas, não uma
 * palavra e reticências) com as ações que fazem sentido PARA AQUELA Lacuna:
 * levá-la ao pé da conversa, abrir o campo que a resolve, pedir a redação ao
 * assistente, ou dizer que não se aplica. O gancho que faltava é exatamente
 * `onSelecionar`: ele troca a Lacuna em foco, e com ela a Resposta Guiada que
 * o `ConversationPane` desenha logo acima do campo de texto.
 *
 * O COMPONENTE É BURRO, DE PROPÓSITO. Ele não chama service, não conhece
 * `interview_id`, não guarda nada do Rascunho. Recebe `gaps` e devolve
 * intenções. Todo estado local aqui é de interface — grupo recolhido, acordeão
 * aberto, rodapé de dispensadas expandido — e some no unmount sem consequência.
 * Foi assim que a tela ficou testável sem subir servidor.
 */

/** Largura da coluna em tela larga. Abaixo disso o chamador manda `compacto`. */
const LARGURA = 320;

/**
 * As cores do validador traduzidas para o semáforo do professor. Vivem aqui e
 * não no `Interview.tsx` porque este é o único lugar que ainda desenha
 * prioridade de Lacuna — duas tabelas para o mesmo domínio fechado é uma a mais
 * do que existe, e a duplicada é a que diverge.
 */
export const COR_PRIORIDADE: Record<GapPriority, string> = {
  P0: palette.dangerText,
  P1a: palette.warning,
  P1b: palette.warning,
  P2: palette.textMuted,
};

/**
 * P0/P1a/P1b/P2 é a nomenclatura do validador, não a do professor. Ele precisa
 * saber uma coisa só: se aquilo impede de aplicar ou não.
 */
export const CHAVE_PRIORIDADE: Record<GapPriority, string> = {
  P0: "interview_priority_required",
  P1a: "interview_priority_recommended",
  P1b: "interview_priority_recommended",
  P2: "interview_priority_optional",
};

/**
 * Qual aba resolve esta Lacuna. É a ÚNICA parte deste arquivo que sabe de
 * domínio, e está isolada e exportada para poder ser lida (e testada) sem
 * montar React.
 *
 * O mapa é sobre `caminho`, o nó do Rascunho a que a Lacuna se refere, porque
 * é o dado estável: o `codigo` do validador pode ganhar irmãos (D08 nasceu
 * depois de D07) e o texto da pergunta é redigido para o humano, não para nós.
 *
 *   contém `detection`            -> 2  Regras da IA   (D01–D06: o que a IA vê/ouve)
 *   `checklist.`/`skill.`/`item.` -> 1  Checklist      (E06–E09, D07, D08)
 *   ou qualquer Lacuna com `ref`     (ref só existe em Habilidade e Item)
 *   `scenario.`, `name`,             -> 0  Cenário         (E01, E03, E04, C02)
 *     `instructions`
 *   resto                         -> 3  Conversa        (E02, C01, E05, C03)
 *
 * A ORDEM DOS TESTES IMPORTA. `checklist.skills[0].items[1].detection` casa com
 * os dois primeiros ramos; detecção vence porque é lá que aquele texto se
 * escreve. Já `...items[1].question` (D07, enunciado longo demais) não contém
 * `detection` e cai certo no Checklist.
 *
 * O RESTO CAI NA CONVERSA E ISSO NÃO É DESISTIR. `theme_id`, `competence_id` e
 * o `scenario` pelado de E05 são escolha de catálogo ou enum: o servidor já
 * manda a `forma` com as opções prontas, e a Resposta Guiada as desenha no pé
 * da conversa. Mandar o professor para um `<select>` em outra aba seria trocar
 * a resposta de um clique por uma viagem.
 */
export function abaDaLacuna(g: InterviewGap): number {
  const c = g.caminho;

  if (c.includes("camera2")) return 4;
  if (c.includes("detection")) return 2;
  if (c.startsWith("checklist.") || c.startsWith("skill.") || c.startsWith("item.") || g.ref) {
    return 1;
  }
  if (c.startsWith("scenario.") || c === "name" || c === "instructions") return 0;

  return 3;
}

/**
 * `codigo` sozinho repete: D04 nasce uma vez por Item mal descrito. O `ref` é
 * a âncora estável que o servidor usa no próprio código de dispensa.
 */
const chaveDaLacuna = (g: InterviewGap) => `${g.codigo}:${g.ref || ""}`;

/**
 * Três grupos, na ordem em que atrapalham. P1a e P1b caem no mesmo balde
 * porque a distinção entre eles é do validador (uma vira Lacuna antes da
 * outra), não do professor: `CHAVE_PRIORIDADE` já traduz as duas para
 * "recomendado".
 */
const GRUPOS: { chave: string; rotulo: string; prioridades: GapPriority[] }[] = [
  { chave: "P0", rotulo: "interview_gap_group_required", prioridades: ["P0"] },
  { chave: "P1", rotulo: "interview_gap_group_recommended", prioridades: ["P1a", "P1b"] },
  { chave: "P2", rotulo: "interview_gap_group_optional", prioridades: ["P2"] },
];

/** Curva de saída sem repique: o material acomoda, não pula. */
const SAIDA = "cubic-bezier(0.16, 1, 0.3, 1)";

/**
 * O código cru (`D04 · checklist.skills[0].items[2].detection.not_done_when`)
 * mora no `title` e em lugar nenhum mais. É affordance de suporte — "me diz o
 * que aparece quando você para o mouse em cima" — e ruído para todos os outros.
 */
function ChipPrioridade({ g }: { g: InterviewGap }) {
  const { t } = useTranslation();

  return (
    <Chip
      size="small"
      variant="outlined"
      label={t(CHAVE_PRIORIDADE[g.prioridade])}
      title={`${g.codigo} · ${g.caminho}`}
      sx={{
        height: 18,
        fontSize: 10,
        letterSpacing: 0.3,
        color: COR_PRIORIDADE[g.prioridade],
        borderColor: COR_PRIORIDADE[g.prioridade],
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
}

/**
 * Botão de ação da linha. Denso porque são até quatro em 280px úteis: sem
 * moldura, separados por peso e cor, não por caixinha.
 *
 * POR QUE AS SECUNDÁRIAS NÃO SOMEM NO REPOUSO. A alternativa óbvia —
 * `opacity: 0` até o hover — foi rejeitada duas vezes: quem navega por teclado
 * ganha um alvo focável e invisível até já estar nele, e quem usa mouse nunca
 * descobre "Pedir à IA" sem passar por cima por acaso. Aqui elas ficam sempre
 * legíveis em `textMuted` (6.4:1 sobre a superfície) e sobem para a cor da
 * marca no `:hover` e no `:focus-within` da linha inteira.
 */
function Acao({
  rotulo,
  ajuda,
  principal,
  disabled,
  onClick,
}: {
  rotulo: string;
  ajuda: string;
  principal?: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="small"
      title={ajuda}
      disabled={disabled}
      onClick={onClick}
      className={principal ? undefined : "acao-secundaria"}
      sx={{
        minWidth: 0,
        px: 0.75,
        py: 0,
        fontSize: 11,
        lineHeight: 1.7,
        fontWeight: principal ? 700 : 500,
        textTransform: "none",
        color: principal ? palette.brand : palette.textMuted,
        "&:hover": { color: palette.brand, background: palette.brandSoft },
      }}
    >
      {rotulo}
    </Button>
  );
}

interface LinhaProps {
  g: InterviewGap;
  selecionada: boolean;
  busy: boolean;
  aplicada: boolean;
  onSelecionar: (g: InterviewGap) => void;
  onIrParaCampo: (g: InterviewGap) => void;
  onPedirAjuda: (g: InterviewGap) => void;
  onDispensar: (g: InterviewGap) => void;
}

function LinhaLacuna({
  g,
  selecionada,
  busy,
  aplicada,
  onSelecionar,
  onIrParaCampo,
  onPedirAjuda,
  onDispensar,
}: LinhaProps) {
  const { t } = useTranslation();

  const aba = abaDaLacuna(g);
  // A `forma` é a autoridade do servidor sobre COMO se responde. Derivar isso
  // de novo aqui (por família de código, por caminho) criaria a segunda regra
  // que sempre diverge da primeira — o mesmo erro que `detectabilidade.ts`
  // documenta no cabeçalho dela.
  const escolhaPronta = g.forma.tipo !== "texto";

  return (
    <Box
      component="li"
      sx={{
        listStyle: "none",
        px: 1.5,
        py: 1,
        borderLeft: `3px solid ${selecionada ? palette.brand : "transparent"}`,
        background: selecionada ? palette.brandSoft : "transparent",
        transition: `background 180ms ${SAIDA}`,
        "&:not(:first-of-type)": { borderTop: `1px solid ${palette.border}` },
        "&:hover, &:focus-within": {
          background: selecionada ? palette.brandSoft : palette.surface,
        },
        "&:hover .acao-secundaria, &:focus-within .acao-secundaria": {
          color: palette.brand,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <ChipPrioridade g={g} />
        {/* O elo com a conversa, dito em voz alta: esta é a que está lá embaixo. */}
        {selecionada && !aplicada && (
          <Typography
            variant="caption"
            sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, color: palette.brand }}
          >
            {t("interview_gap_in_focus")}
          </Typography>
        )}
      </Box>

      {/* Texto completo da pergunta. `motivo` é a justificativa técnica do validador
          ("`scenarios.goal_scene` é NOT NULL"): útil no hover, atravancada na
          lista. */}
      <Typography
        className="interview-gap-question"
        variant="body2"
        title={g.motivo}
        sx={{
          overflowWrap: "anywhere",
          fontSize: 13,
          lineHeight: 1.4,
          color: palette.text,
        }}
      >
        {renderTextoComNegrito(g.pergunta)}
      </Typography>

      {/* Depois de aplicada a Entrevista vira registro de autoria: a lista
          continua legível, mas nada aqui muda o Rascunho. */}
      {!aplicada && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25, mt: 0.75, ml: -0.75 }}>
          <Acao
            principal
            rotulo={escolhaPronta ? t("interview_gap_pick") : t("interview_gap_answer")}
            ajuda={escolhaPronta ? t("interview_gap_pick_hint") : t("interview_gap_answer_hint")}
            disabled={busy}
            onClick={() => onSelecionar(g)}
          />

          {/* Só quando existe outro lugar para ir. Na aba 3 o professor já
              está na conversa; para Cenário, Checklist e Regras da IA, o
              botão navega até o campo correspondente. */}
          {aba !== 3 && (
            <Acao
              rotulo={t("interview_gap_open_field")}
              ajuda={t("interview_gap_open_field_hint")}
              disabled={busy}
              onClick={() => onIrParaCampo(g)}
            />
          )}

          {/* Pedir ao modelo o que o servidor já sabe reintroduz o
              não-determinismo que a `forma` existe para tirar: onde a resposta
              é id de catálogo ou enum, a IA só teria como chutar. Onde é
              redação — as sete Lacunas de Detecção e o resto do texto livre —
              é justamente o que ela faz bem. */}
          {g.forma.tipo === "texto" && (
            <Acao
              rotulo={t("interview_gap_ask_ai")}
              ajuda={t("interview_gap_ask_ai_hint")}
              disabled={busy}
              onClick={() => onPedirAjuda(g)}
            />
          )}

          {/* Antes isto só existia para a Lacuna corrente; para dispensar a
              quarta da lista era preciso responder as três da frente. P0 nunca
              chega aqui — o servidor não marca bloqueante como dispensável. */}
          {g.dispensavel && (
            <Acao
              rotulo={t("interview_waive")}
              ajuda={t("interview_gap_waive_hint")}
              disabled={busy}
              onClick={() => onDispensar(g)}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * Um grupo de prioridade. O cabeçalho gruda no topo durante a rolagem: com 13
 * Lacunas o professor perde de vista se ainda está no bloco que impede aplicar.
 */
function Grupo({
  rotulo,
  cor,
  gaps,
  recolhido,
  onAlternar,
  linha,
}: {
  rotulo: string;
  cor: string;
  gaps: InterviewGap[];
  recolhido: boolean;
  onAlternar: () => void;
  linha: (g: InterviewGap) => React.ReactNode;
}) {
  const { t } = useTranslation();

  if (gaps.length === 0) return null;

  return (
    <Box component="section">
      <ButtonBase
        onClick={onAlternar}
        aria-expanded={!recolhido}
        title={t("interview_gap_group_toggle") ?? undefined}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 0.5,
          px: 1.25,
          py: 0.75,
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: palette.background,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: cor,
            transform: recolhido ? "rotate(-90deg)" : "none",
            transition: `transform 180ms ${SAIDA}`,
          }}
        />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.6, fontSize: 10.5, color: cor }}
        >
          {t(rotulo).toUpperCase()}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 10.5, color: palette.textMuted }}>
          {gaps.length}
        </Typography>
      </ButtonBase>

      <Collapse in={!recolhido} unmountOnExit>
        <Box component="ul" sx={{ m: 0, p: 0 }}>
          {gaps.map(linha)}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * As dispensadas, no pé do painel.
 *
 * O rótulo do chip é a string opaca que o servidor mandou, e isso é
 * deliberado apesar da regra de nunca mostrar código cru: dispensada a Lacuna,
 * ela sai de `gaps` e a pergunta em português vai junto. Sobra o `waiver` como
 * único handle. Rotular todas de "ponto dispensado" tornaria revogar um sorteio
 * entre chips idênticos — pior do que um código feio e pequeno.
 */
function Dispensadas({
  waivers,
  busy,
  aplicada,
  onRevogar,
}: {
  waivers: string[];
  busy: boolean;
  aplicada: boolean;
  onRevogar: (waiver: string) => void;
}) {
  const { t } = useTranslation();
  const [aberto, setAberto] = useState(false);

  if (waivers.length === 0) return null;

  // Até três cabem em duas fileiras de chip sem empurrar a lista para cima.
  // Acima disso viram um resumo clicável.
  const muitas = waivers.length > 3;
  const mostrando = !muitas || aberto;

  const chips = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
      {waivers.map((w) => (
        <Chip
          key={w}
          size="small"
          variant="outlined"
          label={w}
          title={t("interview_waived_undo")}
          onDelete={busy || aplicada ? undefined : () => onRevogar(w)}
          sx={{ height: 20, fontSize: 11, maxWidth: "100%" }}
        />
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: 1.5,
        py: 1,
        borderTop: `1px solid ${palette.border}`,
        background: palette.muted,
      }}
    >
      {muitas ? (
        <ButtonBase
          onClick={() => setAberto((v) => !v)}
          aria-expanded={aberto}
          sx={{ gap: 0.5, borderRadius: 1 }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              color: palette.textMuted,
              transform: aberto ? "none" : "rotate(-90deg)",
              transition: `transform 180ms ${SAIDA}`,
            }}
          />
          <Typography variant="caption" sx={{ color: palette.textMuted }}>
            {`${waivers.length} ${t(
              waivers.length === 1 ? "interview_gap_waived_one" : "interview_gap_waived_many"
            )}`}
          </Typography>
        </ButtonBase>
      ) : (
        <Typography variant="caption" sx={{ color: palette.textMuted }}>
          {t("interview_waived")}
        </Typography>
      )}

      <Collapse in={mostrando} unmountOnExit>
        {chips}
      </Collapse>
    </Box>
  );
}

export interface PainelLacunasProps {
  gaps: InterviewGap[];
  waivers: string[];
  /** A Lacuna em foco na conversa. Default do chamador = gaps[0]. */
  selecionada: InterviewGap | null;
  busy: boolean;
  aplicada: boolean;
  /** Põe esta Lacuna no pé da conversa: vira a pergunta corrente e a Resposta Guiada passa a ser a dela. */
  onSelecionar: (g: InterviewGap) => void;
  /** Leva ao campo que resolve a Lacuna (troca de aba). */
  onIrParaCampo: (g: InterviewGap) => void;
  /** Manda uma frase ao assistente pedindo que ELE resolva esta Lacuna. Vira turno. */
  onPedirAjuda: (g: InterviewGap) => void;
  onDispensar: (g: InterviewGap) => void;
  onRevogar: (waiver: string) => void;
  /** true = renderiza como acordeão recolhível (telas estreitas). */
  compacto?: boolean;
}

export default function PainelLacunas(p: PainelLacunasProps): JSX.Element {
  const { t } = useTranslation();
  // Único estado do componente, e é de interface: quais grupos o professor
  // fechou. Recolher "Opcionais" para ficar só com o que impede de aplicar é o
  // uso que motivou isto.
  const [recolhidos, setRecolhidos] = useState<Record<string, boolean>>({});

  const bloqueantes = p.gaps.filter((g) => g.prioridade === "P0");
  const sugeridas = p.gaps.length - bloqueantes.length;

  // Agrupa SEM reordenar dentro do grupo: `gaps` já vem na ordem do validador,
  // que é a ordem em que as perguntas fazem sentido (nome antes de tema, tema
  // antes de seções). Um `sort` aqui desmontaria essa sequência em silêncio.
  const grupos = useMemo(
    () =>
      GRUPOS.map((grupo) => ({
        ...grupo,
        cor: COR_PRIORIDADE[grupo.prioridades[0]],
        itens: p.gaps.filter((g) => grupo.prioridades.includes(g.prioridade)),
      })),
    [p.gaps]
  );

  /** "3 respostas obrigatórias · 10 melhorias sugeridas" — o tom já usado na tela. */
  const resumo =
    (bloqueantes.length > 0
      ? `${bloqueantes.length} ${t(
          bloqueantes.length === 1
            ? "interview_pending_required_one"
            : "interview_pending_required_many"
        )}`
      : t("interview_no_gaps")) +
    (sugeridas > 0
      ? ` · ${sugeridas} ${t(
          sugeridas === 1 ? "interview_pending_optional_one" : "interview_pending_optional_many"
        )}`
      : "");

  const linha = (g: InterviewGap) => (
    <LinhaLacuna
      key={chaveDaLacuna(g)}
      g={g}
      selecionada={
        !!p.selecionada && chaveDaLacuna(p.selecionada) === chaveDaLacuna(g)
      }
      busy={p.busy}
      aplicada={p.aplicada}
      onSelecionar={p.onSelecionar}
      onIrParaCampo={p.onIrParaCampo}
      onPedirAjuda={p.onPedirAjuda}
      onDispensar={p.onDispensar}
    />
  );

  /**
   * Comemora sem mentir. "Pronto para aplicar" é o que o validador garante:
   * as colunas NOT NULL estão preenchidas e os pontos fecham em 100. Não é
   * promessa de que o Cenário está bom — e prometer isso aqui seria assinar
   * embaixo de um Checklist que ninguém leu.
   */
  const vazio = (
    <Box sx={{ px: 2, py: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.emeraldDeep }}>
        {t("interview_gap_empty_title")}
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.5, color: palette.textMuted, lineHeight: 1.5 }}
      >
        {t("interview_gap_empty_note")}
      </Typography>
    </Box>
  );

  const lista =
    p.gaps.length === 0 ? (
      vazio
    ) : (
      <>
        {grupos.map((grupo) => (
          <Grupo
            key={grupo.chave}
            rotulo={grupo.rotulo}
            cor={grupo.cor}
            gaps={grupo.itens}
            recolhido={!!recolhidos[grupo.chave]}
            onAlternar={() =>
              setRecolhidos((r) => ({ ...r, [grupo.chave]: !r[grupo.chave] }))
            }
            linha={linha}
          />
        ))}
      </>
    );

  const rodape = (
    <Dispensadas
      waivers={p.waivers}
      busy={p.busy}
      aplicada={p.aplicada}
      onRevogar={p.onRevogar}
    />
  );

  // ---- tela estreita: acordeão recolhido, acima da conversa ---------------
  //
  // Recolhido por padrão porque em 900px de largura a conversa é a tela: o
  // painel aberto empurraria as bolhas para fora antes de o professor ler uma.
  // O resumo fica na barra, então mesmo fechado ele diz quanto falta.
  if (p.compacto) {
    return (
      <Accordion
        disableGutters
        elevation={0}
        square
        sx={{
          flexShrink: 0,
          background: palette.brandWash,
          borderBottom: `1px solid ${palette.border}`,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: palette.brandDark }} />}
          sx={{ minHeight: 40, px: 1.5, "& .MuiAccordionSummary-content": { my: 0.75 } }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, letterSpacing: 0.6, color: palette.brandDark }}
            >
              {t("interview_next_question")}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: palette.textMuted, minWidth: 0 }}
            >
              {resumo}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, background: palette.background }}>
          {/* Teto de altura: aberto no celular, o painel ainda deixa a conversa
              à vista, e rola por dentro em vez de rolar a página. */}
          <Box sx={{ maxHeight: 280, overflowY: "auto" }}>{lista}</Box>
          {rodape}
        </AccordionDetails>
      </Accordion>
    );
  }

  // ---- tela larga: coluna fixa de 320px ao lado da conversa ---------------
  return (
    <Box
      component="aside"
      sx={{
        width: LARGURA,
        flexShrink: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderLeft: `1px solid ${palette.border}`,
        background: palette.background,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1.5,
          py: 1.25,
          background: palette.brandWash,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 700,
            letterSpacing: 0.6,
            color: palette.brandDark,
          }}
        >
          {t("interview_next_question")}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: palette.textMuted }}>
          {resumo}
        </Typography>
        {/* A instrução que faltava. Uma linha, e só enquanto houver o que fazer. */}
        {p.gaps.length > 0 && !p.aplicada && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.5, color: palette.brandDark, lineHeight: 1.4 }}
          >
            {t("interview_gap_hint")}
          </Typography>
        )}
      </Box>

      {/* A coluna rola SOZINHA. É metade da resposta ao "gostinho de scroll no
          fundo da página": ninguém mais precisa rolar o documento inteiro para
          ver a décima terceira Lacuna. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{lista}</Box>

      {rodape}
    </Box>
  );
}
